import { useRef, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { authClient } from "../../lib/authClient";

type OtpStep = "email" | "otp";

export function EmailOtpAuth() {
  const navigate = useNavigate();
  const [step, setStep] = useState<OtpStep>("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleSendOtp = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!email) {
      setError("Por favor, insira seu email.");
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      // Envia OTP para o email
      const { error: sendError } = await authClient.emailOtp
        .sendVerificationOtp({
          email,
          type: "sign-in",
        });

      if (sendError) {
        setError(
          sendError.message ?? "Erro ao enviar código. Tente novamente.",
        );
        return;
      }

      // Avança para o passo de inserir OTP
      setStep("otp");

      // Foca no primeiro input de OTP após um pequeno delay
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    } catch (error) {
      console.error("Erro ao enviar OTP:", error);
      setError("Erro ao enviar código. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value && !/^\d+$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setError(null);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();

    if (!/^\d+$/.test(pastedData)) {
      return;
    }

    const digits = pastedData.slice(0, 6).split("");
    const newOtp = [...otp];
    digits.forEach((digit, index) => {
      if (index < 6) {
        newOtp[index] = digit;
      }
    });
    setOtp(newOtp);

    const nextEmptyIndex = newOtp.findIndex((val) => !val);
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus();
    } else {
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerifyOtp = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const otpCode = otp.join("");

    if (otpCode.length !== 6) {
      setError("Por favor, insira o código completo de 6 dígitos.");
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      // Faz login com o OTP
      const { error: signInError } = await authClient.signIn.emailOtp({
        email,
        otp: otpCode,
      });

      if (signInError) {
        setError(signInError.message ?? "Código inválido ou expirado.");
        return;
      }

      // Redireciona para o perfil após sucesso
      navigate("/profile", { replace: true });
    } catch (error) {
      console.error("Erro ao verificar OTP:", error);
      setError("Erro ao verificar código. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      const { error: resendError } = await authClient.emailOtp
        .sendVerificationOtp({
          email,
          type: "sign-in",
        });

      if (resendError) {
        setError(resendError.message ?? "Erro ao reenviar código.");
        return;
      }

      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } catch (error) {
      console.error("Erro ao reenviar OTP:", error);
      setError("Erro ao reenviar código. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeEmail = () => {
    setStep("email");
    setOtp(["", "", "", "", "", ""]);
    setError(null);
  };

  if (step === "otp") {
    return (
      <div className="auth-method-content">
        <p className="auth-method-subtitle">
          Digite o código de 6 dígitos enviado para <strong>{email}</strong>
        </p>

        <form className="otp-form" onSubmit={handleVerifyOtp}>
          <div className="otp-inputs" onPaste={handlePaste}>
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="otp-input"
              />
            ))}
          </div>

          {error && <p className="auth-error">{error}</p>}

          <button
            type="submit"
            className="primary-button"
            disabled={isSubmitting || otp.join("").length !== 6}
          >
            {isSubmitting ? "Verificando..." : "Entrar"}
          </button>

          <div className="otp-actions">
            <button
              type="button"
              onClick={handleResendOtp}
              className="link-button"
              disabled={isSubmitting}
            >
              Reenviar código
            </button>
            <button
              type="button"
              onClick={handleChangeEmail}
              className="link-button"
            >
              Alterar email
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="auth-method-content">
      <p className="auth-method-subtitle">
        Insira seu email para receber um código de acesso
      </p>

      <form className="auth-form" onSubmit={handleSendOtp}>
        <label className="auth-field">
          <span>Email</span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="seu@email.com"
            autoComplete="email"
            name="email"
            required
          />
        </label>

        {error && <p className="auth-error">{error}</p>}

        <button
          type="submit"
          className="primary-button"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Enviando..." : "Enviar código"}
        </button>
      </form>
    </div>
  );
}
